import { COMPANY_DETAILS, LEGAL_PATHS, ORBIS_PRODUCTS, PRODUCT_DOCS_URL } from '../lib/links';
import { BLOG_PATH } from '../lib/blog';
import footerLogo from '../assets/v2-footer-logo.svg';

/**
 * Footer — Figma node 2:502. Rebuilt in the 2026-07 refresh from a single row
 * into a four-column block plus a tinted bottom bar.
 *
 * Design values (at the 1920 reference width):
 *  · block    border-t #F2F4F7, pt-80, gap-46; inner px-133
 *  · logo     Figma has a 254×254 square mark (node 2:505); we use the wide
 *             dark-text lockup supplied by the client instead (316×114)
 *  · headings Inter 700 18/24, black, UPPERCASE
 *  · links    Inter 400 18/24, black; Solutions column gap-20, column gap-28
 *  · company  Inter 400 18/28, black, max-w-376
 *  · bar      bg #F2F4F7, px-133 py-24, rounded-t-12;
 *             Inter 400 14.875/20.3 #212121, legal links gap-28
 *
 * The logo is the wide lockup in its DARK-TEXT form, supplied by the client
 * (2026-07-27). The header's copy of this lockup cannot be reused here: its
 * "WISDOM" wordmark is white for the dark navbar, and on this white footer only
 * the orbital mark and the orange X survived. This version is navy and also
 * carries the two taglines, so it is sized by width rather than height to keep
 * that small type readable.
 *
 * Two things about the supplied .svg, neither breaking:
 *  · It is a base64 PNG in an SVG wrapper, not true vector, so it does not scale
 *    losslessly and is ~25% larger than the bitmap alone would be.
 *  · It carries baked-in whitespace — measured ink fills only 73% of its height
 *    (~9.5px above, ~21px below in its own 316×114 units), so the mark renders
 *    smaller than its box and sits slightly high. Sized by width to compensate.
 *    A tight-cropped export would let it read ~35% larger in the same footprint.
 *
 * The Solutions column and the nav dropdown share ORBIS_PRODUCTS, so they cannot
 * drift. Those product names still have no destinations and render as plain text.
 * "Product Docs" and the two legal links are real links — those pages exist.
 */

function ColumnHeading({ children }: { children: string }) {
  return (
    <h2 className="m-0 text-base font-bold uppercase leading-6 text-black lg:text-lg">
      {children}
    </h2>
  );
}

export function Footer() {
  return (
    <footer className="w-full border-t border-landing-footer-line bg-white pt-12 lg:pt-20">
      <div className="mx-auto flex max-w-[1920px] flex-col gap-10 lg:gap-[46px]">
        <div className="px-5 sm:px-6 md:px-8 lg:px-10 xl:px-16 2xl:px-[133px]">
          <div className="flex flex-col items-start gap-10 md:flex-row md:justify-between md:gap-8">
            <img
              src={footerLogo}
              alt="WisdomX Orbis — System Closure Intelligence Platform"
              width={316}
              height={114}
              className="h-auto w-[240px] shrink-0 lg:w-[340px]"
            />

            {/* Solutions */}
            <div className="flex shrink-0 flex-col gap-5 lg:gap-7">
              <ColumnHeading>Solutions</ColumnHeading>
              <ul className="flex list-none flex-col gap-4 p-0 lg:gap-5">
                {ORBIS_PRODUCTS.map((item) => (
                  <li key={item} className="text-base leading-6 text-black lg:text-lg">
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Resource */}
            <div className="flex shrink-0 flex-col gap-5 lg:gap-7">
              <ColumnHeading>Resource</ColumnHeading>
              <a
                href={PRODUCT_DOCS_URL}
                target="_blank"
                rel="noreferrer"
                className="text-base leading-6 text-black hover:underline lg:text-lg"
              >
                Product Docs
              </a>
              <a
                href={BLOG_PATH}
                className="text-base leading-6 text-black hover:underline lg:text-lg"
              >
                Blog
              </a>
            </div>

            {/* Company */}
            <div className="flex w-full flex-col gap-6 md:max-w-[376px] lg:gap-8">
              <ColumnHeading>Company</ColumnHeading>
              <div className="flex flex-col gap-4 text-base leading-7 text-black lg:text-lg">
                <p className="m-0">
                  {COMPANY_DETAILS.legalName}
                  <br />
                  {COMPANY_DETAILS.companyNumber}
                </p>
                <p className="m-0">
                  {COMPANY_DETAILS.officeLabel}
                  <br />
                  {COMPANY_DETAILS.office}
                </p>
                <p className="m-0">{COMPANY_DETAILS.incorporated}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="flex flex-col items-center gap-3 rounded-t-xl bg-landing-footer-line px-5 py-5 text-center sm:px-6 md:flex-row md:justify-between md:gap-6 md:text-left lg:px-10 lg:py-6 xl:px-16 2xl:px-[133px]">
          <p className="m-0 text-[14.875px] leading-[20.3px] text-landing-footer-text">
            © 2026 WisdomX Ltd — All Rights Reserved
          </p>
          <div className="flex items-center gap-7 text-[14.875px] leading-[20.3px] text-landing-footer-text">
            <a href={LEGAL_PATHS.privacy} className="whitespace-nowrap hover:underline">
              Privacy Policy
            </a>
            <a href={LEGAL_PATHS.terms} className="whitespace-nowrap hover:underline">
              Terms of Service
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
